import React, { useState, useEffect, useCallback } from 'react';
import { salesGoalsAPI, brainDigestAPI, voiceFlowsAPI, aiTemplatesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Brain, Target, Zap, TrendingUp, Mail, Phone, Sparkles,
  ChevronRight, Clock, CheckCircle, Loader2, Send,
  FileText, Download, Activity, BarChart3
} from 'lucide-react';
import { sanitizeHtml } from '../../lib/sanitize';
import BrainHealthCard from '../../components/brain/BrainHealthCard';
import AgentChannelsCard from '../../components/dashboard/AgentChannelsCard';

const formatCurrency = (v) => {
  // Iter 60 — explicit "no data" placeholder instead of misleading $0 when
  // the backend hasn't populated pipeline value yet.
  if (v === null || v === undefined) return '—';
  if (v === 0) return '—';
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

export default function BrainCommandCenter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState({ goals: [], pipeline: {} });
  const [digest, setDigest] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [recentFlows, setRecentFlows] = useState([]);
  const [digestLoading, setDigestLoading] = useState(false);
  // Iter 60.3 — inline create-goal dialog (no /settings/sales-goals page exists)
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [goalForm, setGoalForm] = useState({ goal_type: 'revenue', target_value: '', period: 'monthly' });
  const [creatingGoal, setCreatingGoal] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [goalsRes, templatesRes] = await Promise.all([
        salesGoalsAPI.getSummary(),
        aiTemplatesAPI.getInstalled(),
      ]);
      setGoals(goalsRes.data || { goals: [], pipeline: {} });
      setTemplates(templatesRes.data?.templates || templatesRes.data || []);
      // Digest preview is LAZY (Load Preview button): auto-loading fired a 30s+
      // LLM call on every page mount (x2 in StrictMode), exhausting the browser
      // connection pool and starving other requests (e.g. Go-LIVE readiness).
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await brainDigestAPI.preview(7);
      setDigest(res.data);
    } catch { /* silent */ }
    finally { setDigestLoading(false); }
  };

  // Iter 60.3 — inline goal creation (no dedicated page exists in routing)
  const handleCreateGoal = async () => {
    const target = parseFloat(goalForm.target_value);
    if (!target || target <= 0) {
      toast.error('Enter a valid target value');
      return;
    }
    setCreatingGoal(true);
    try {
      await salesGoalsAPI.create({
        goal_type: goalForm.goal_type,
        target_value: target,
        period: goalForm.period,
        status: 'active',
      });
      toast.success('Goal created');
      setShowGoalDialog(false);
      setGoalForm({ goal_type: 'revenue', target_value: '', period: 'monthly' });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create goal');
    } finally {
      setCreatingGoal(false);
    }
  };

  const pipeline = goals.pipeline || {};

  if (loading) return (
    <div className="space-y-4 p-6" data-testid="brain-command-skeleton">
      <div className="h-7 w-56 bg-muted rounded animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="p-5 rounded-lg border space-y-2">
            <div className="h-4 w-24 bg-muted/60 rounded animate-pulse" />
            <div className="h-8 w-16 bg-muted rounded animate-pulse" />
            <div className="h-3 w-20 bg-muted/40 rounded animate-pulse" />
          </div>
        ))}
      </div>
      <div className="rounded-lg border p-6 space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
            <div className="h-10 w-10 bg-muted rounded-full animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-40 bg-muted rounded animate-pulse" />
              <div className="h-3 w-56 bg-muted/40 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="brain-command-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="command-center-title">
            <Brain className="h-6 w-6 text-cyan-500" /> Brain Command Center
          </h1>
          <p className="text-muted-foreground text-sm">Unified view of your AI-powered sales engine</p>
        </div>
      </div>

      {/* Activation & health — surfaces SIMULATED / mailbox / subscription blockers */}
      <BrainHealthCard />

      {/* Per-agent channel controls */}
      <AgentChannelsCard />

      {/* Pipeline Overview Cards */}
      <div className="grid gap-4 md:grid-cols-4" data-testid="pipeline-stats">
        <StatCard testid="stat-total-leads" icon={Target} label="Total Leads" value={pipeline.total_leads || 0} color="text-blue-500" />
        <StatCard testid="stat-hot-leads" icon={Zap} label="Hot Leads" value={pipeline.hot_leads || 0} sub="Score 75+" color="text-red-500" />
        <StatCard testid="stat-warm-leads" icon={TrendingUp} label="Warm Leads" value={pipeline.warm_leads || 0} sub="Score 50+" color="text-amber-500" />
        <StatCard testid="pipeline-value-stat" icon={BarChart3} label="Pipeline Value" value={formatCurrency(pipeline.total_value)} color="text-emerald-500" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Sales Goals */}
        <Card className="lg:col-span-2" data-testid="goals-section">
          <CardHeader className="flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Target className="h-4 w-4 text-cyan-500" /> Sales Goals</CardTitle>
              <CardDescription>AI-generated revenue targets</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/brain/digest')} className="text-xs">
              View Digest <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {goals.goals?.length === 0 ? (
              <div className="text-center py-6" data-testid="goals-empty-state">
                <Target className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground mb-3">No active goals yet — set one to track AI-driven progress.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowGoalDialog(true)}
                  className="text-xs"
                  data-testid="goals-create-cta"
                >
                  <Sparkles className="h-3 w-3 mr-1" /> Set a goal
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {goals.goals.map((goal) => {
                  const progress = goal.target_value > 0 ? Math.min((goal.current_value / goal.target_value) * 100, 100) : 0;
                  return (
                    <div key={goal.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium truncate flex-1">{goal.title}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {formatCurrency(goal.current_value)} / {formatCurrency(goal.target_value)}
                        </span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{goal.period}</Badge>
                        {goal.ai_generated && <Badge variant="secondary" className="text-[10px]"><Sparkles className="h-2.5 w-2.5 mr-0.5" /> AI</Badge>}
                        {goal.metadata && (
                          <span className="text-[10px] text-muted-foreground">{goal.metadata.hot_leads || 0} hot, {goal.metadata.warm_leads || 0} warm leads</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quick Pipeline Stats */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {pipeline.pending_tasks || 0} pending tasks</span>
              <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {pipeline.pending_followups || 0} follow-ups</span>
            </div>
          </CardContent>
        </Card>

        {/* Digest Summary */}
        <Card data-testid="digest-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4 text-violet-500" /> Latest Digest</CardTitle>
            <CardDescription>Weekly brain activity summary</CardDescription>
          </CardHeader>
          <CardContent>
            {!digest ? (
              <div className="text-center py-4">
                <Button variant="outline" size="sm" onClick={loadDigest} disabled={digestLoading} data-testid="load-digest-btn">
                  {digestLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                  Load Preview
                </Button>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                {digest.stats ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <MiniStat label="Decisions" value={digest.stats.total_decisions} />
                      <MiniStat label="Active Leads" value={digest.stats.active_leads} />
                      <MiniStat label="AI Tasks" value={digest.stats.ai_tasks_created} />
                      <MiniStat label="Drafts" value={digest.stats.ai_drafts_created} />
                    </div>
                    {digest.stats.top_leads?.length > 0 && (
                      <div className="border-t pt-2">
                        <span className="text-xs font-medium">Top Leads:</span>
                        {digest.stats.top_leads.slice(0, 3).map((l, i) => (
                          <div key={i} className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span className="truncate">{l.name}</span>
                            <span className="font-medium">{l.ai_score}/100</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="prose dark:prose-invert max-w-none text-xs" dangerouslySetInnerHTML={{ __html: sanitizeHtml(digest.html_content?.substring(0, 500) || 'No data') }} />
                )}
                <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => navigate('/brain/digest')}>
                  Full Digest Settings <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Active Templates */}
        <Card data-testid="templates-section">
          <CardHeader className="flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> Active Templates</CardTitle>
              <CardDescription>{templates.length} installed</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/brain/templates')} className="text-xs">
              Browse <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-xs text-muted-foreground mb-2">No templates installed</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/brain/templates')} className="text-xs">
                  <Download className="h-3 w-3 mr-1" /> Browse Templates
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {templates.slice(0, 5).map((tpl) => (
                  <div key={tpl.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center">
                      <Sparkles className="h-3.5 w-3.5 text-cyan-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{tpl.name}</p>
                      <p className="text-[10px] text-muted-foreground">{tpl.category}</p>
                    </div>
                    <Badge variant="secondary" className="text-[10px] shrink-0">
                      <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> Active
                    </Badge>
                  </div>
                ))}
                {templates.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center pt-1">+{templates.length - 5} more</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Voice Flows */}
        <Card data-testid="voice-flows-section">
          <CardHeader className="flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Phone className="h-4 w-4 text-emerald-500" /> Voice Call Flows</CardTitle>
              <CardDescription>AI-generated call scripts</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/brain/voice-flows')} className="text-xs">
              Generate <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentFlows.length === 0 ? (
              <div className="text-center py-4">
                <Phone className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-xs text-muted-foreground mb-2">No call flows generated yet</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/brain/voice-flows')} className="text-xs">
                  <FileText className="h-3 w-3 mr-1" /> Generate Script
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {recentFlows.slice(0, 5).map((flow) => (
                  <div key={flow.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{flow.flow_name}</p>
                      <p className="text-[10px] text-muted-foreground">{flow.lead_context?.name} — {new Date(flow.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card data-testid="quick-actions">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" /> Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/brain/digest')} className="text-xs" data-testid="qa-digest">
              <Mail className="h-3 w-3 mr-1" /> Configure Digest
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/brain/voice-flows')} className="text-xs" data-testid="qa-voice">
              <Phone className="h-3 w-3 mr-1" /> Generate Call Script
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/brain/templates')} className="text-xs" data-testid="qa-templates">
              <Download className="h-3 w-3 mr-1" /> Browse Templates
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} className="text-xs" data-testid="qa-dashboard">
              <Brain className="h-3 w-3 mr-1" /> Brain Chat
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Iter 60.3 — Inline Create Goal dialog (no /settings/sales-goals page exists) */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent data-testid="create-goal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Set a sales goal
            </DialogTitle>
            <DialogDescription>
              Track progress toward a measurable target. The AI Brain uses your goal to
              prioritize leads and surface the next-best action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="goal_type">Goal type</Label>
              <Select
                value={goalForm.goal_type}
                onValueChange={(v) => setGoalForm({ ...goalForm, goal_type: v })}
              >
                <SelectTrigger id="goal_type" data-testid="goal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Revenue ($)</SelectItem>
                  <SelectItem value="deals_won">Deals won (count)</SelectItem>
                  <SelectItem value="meetings_booked">Meetings booked</SelectItem>
                  <SelectItem value="leads_qualified">Leads qualified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="target_value">
                Target {goalForm.goal_type === 'revenue' ? '(USD)' : '(count)'}
              </Label>
              <Input
                id="target_value"
                type="number"
                placeholder={goalForm.goal_type === 'revenue' ? 'e.g. 50000' : 'e.g. 10'}
                value={goalForm.target_value}
                onChange={(e) => setGoalForm({ ...goalForm, target_value: e.target.value })}
                data-testid="goal-target-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="period">Time period</Label>
              <Select
                value={goalForm.period}
                onValueChange={(v) => setGoalForm({ ...goalForm, period: v })}
              >
                <SelectTrigger id="period" data-testid="goal-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoalDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreateGoal}
              disabled={creatingGoal || !goalForm.target_value}
              data-testid="goal-create-confirm"
            >
              {creatingGoal && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Create goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <Card data-testid={testid || `stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg bg-muted/50 flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
          {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="p-2 rounded-lg bg-muted/30 text-center">
      <p className="text-lg font-bold">{value || 0}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}
